import { ViewportScroller } from '@angular/common';
import { Injectable, Injector } from '@angular/core';
import { NavigationEnd, NavigationError, NavigationStart, Router, ResolveEnd } from '@angular/router';
import { NAVIGATION_TREE } from '../../config/navigation-tree';
import { SearchCriteria, SortCriterion, MenuNode } from '../angular-ui-common/base-types';
import { debug, log } from './util';

/**
 * Dati di navigazione.
 * Qui sono contenuti, per esempio, i criteri di filtro e ordinamento settati l'ultima volta che si è stati nella pagina corrente. Questi rimangono validi
 * fino ad un eventuale refresh della pagina.
 */
export interface NavigationData {
  searchCriteria?: SearchCriteria
}

/**
 * Questo data service deve rimanere svincolato dall'implementazione tecnica dei sidenav.
 * Non deve contenere riferimenti a material.
 */
@Injectable()
export class RoutingDataService {

  public navigationTree: MenuNode[];

  /**
   * Contiene come chiavi solo gli id dei nodi navigabili e come valori gli array dei nodi dalla root al nodo stesso
   * es:
   * {
   *    user_profiles: [{id: 'iam', ...}, {id: 'user_profiles', ...}],
   *    new_user_profile: [{id: 'iam', ...}, {id: 'user_profiles', ...}, {id: 'new_user_profile', ...}],
   *    edit_user_profile: [{id: 'iam', ...}, {id: 'user_profiles', ...}, {id: 'edit_user_profile', ...}],
   *    ...
   * }
   */
  public flatNavigationTree: {[id: string]: MenuNode[]}

  /**
   * Contiene i dati di navigazione (es. ultimi filtri applicati) relativi ad ogni singola pagina navigabile.
   * Contiene come chiavi solo gli id dei nodi navigabili.
   * es:
   * {
   *    user_profiles: {searchCriteria?: ..., ...}
   *    new_user_profile: {searchCriteria?: ..., ...}
   *    edit_user_profile: {searchCriteria?: ..., ...}
   *    ...
   * }
   */
  public pagesNavigationData: {[id: string]: NavigationData}

  /**
   * L'id della pagina di navigazione corrente (menu e breadcrumbs si basano su questa variabile per evidenziare il nodo corrente). 
   */
  public currentRoutingNodeId: string;
  
  /**
   * es:
   *    [{id: 'iam', ...], [{id: 'user_profiles', ...], [{id: 'user_profile', ...]}]
   */
  public currentBreadcrumbs: MenuNode[];

  /**
   * L'id della voce di menu correntemente selezionata
   */
  public selectedMenuNodeId;

  /**
   * La parte di url corrente che segue la porta (con i placeholder degli url params non sostituiti)
   * es. 
   *      /set-password?p=p
   *      /iam/roles/:id/edit
   */
  public currentUrl : string

  /**
   * La parte di url corrente che segue la porta (con i placeholder degli url params già sostituiti) 
   * es. 
   *      /set-password?p=p
   *      /iam/roles/VOTE/edit
   */
  public currentEffectiveUrl : string

  /**
   * I query params della url corrente
   */
  public currentUrlParams : {[key: string]: string}

  constructor(protected _injector: Injector) {
    this.navigationTree = NAVIGATION_TREE;
    this._iterateOverTreeAndInitOtherDataStructures(NAVIGATION_TREE)

    // Si registra a tutti gli eventi di Routing
    this._injector.get(Router).events.subscribe(event => {

      if (event instanceof NavigationStart) {
        log('RoutingDataService', 'on NavigationStart', 'event', event, 'green', true)
      
      } else if (event instanceof ResolveEnd) { //Non possiamo usare NavigationEnd perchè viene scatenato dopo il costruttore del componente di destinazione
        log('RoutingDataService', 'on ResolveEnd', 'event', event, 'green', true)

        this._injector.get(RoutingDataService).updateRoutingState()
        
        // ATTENZIONE
        // A causa (crediamo) di un bug, le ancore funzionano solo quando ci si trova già sulla
        // pagina che le contiene. Se si proviene da un'altra pagina vengono totalmente ignorate.
        // Qui, se si trova un'ancora, viene forzato lo spostamento verso la stessa, a valle di una
        // navigazione già conclusa.
        const url = event.urlAfterRedirects;
        const segments = url.split('#');
        if(segments.length>1) {
          //Se è presente un'ancora nell'url, allora si sposta sull'ancora
          if(segments.length>0){
            setTimeout(el=>{
              this._injector.get(ViewportScroller).scrollToAnchor(segments[1]);
            },5)
          }
        }
      }
    });
  }

  public setCurrentUrl(url: string) {
    this.currentUrl = url; 
    log('RoutingDataService', 'setCurrentUrl()', 'currentUrl', this.currentUrl, 'green', true)
  }

  /**
   * Viene chiamato da RoutingGuard per settare il nodo di navigazione corrente
   */
  public setCurrentRoutingNode() { 
    this._setCurrentRoutingNodeId(this.currentUrl);
    log('RoutingDataService', 'setCurrentRoutingNode()', 'currentRoutingNodeId', this.currentRoutingNodeId, 'green', true)
  }

  public getCurrentRoutingNode() {
    const row = this.flatNavigationTree[this.currentRoutingNodeId]
    return row[row.length-1];
  }

  public setPageNavigationData(routingNodeId: string, navigationData: NavigationData) {
    this.pagesNavigationData[routingNodeId] = navigationData
  }

  //TODO: chiamare su ON-DESTROY dei grid consumer
  public setCurrentPageNavigationData(navigationData: NavigationData) {
    this.pagesNavigationData[this.currentRoutingNodeId] = navigationData
  }

  public getCurrentPageNavigationData(): NavigationData {
    return this.pagesNavigationData[this.currentRoutingNodeId]
  }

  public navigateToPreviousNode() {
    this._injector.get(Router).navigate([this.currentBreadcrumbs[this.currentBreadcrumbs.length-2].url])

    const node = this.currentBreadcrumbs[this.currentBreadcrumbs.length-2];

    // Attenzione: non bisogna mai navigare verso url con url params non sostituiti opportunamente. Es: /iam/roles/:id/edit
    // In questo caso bisogna prima recuperare i valori dei parametri dalla url corrente (prima della navigazione) e sostituirli nella url del nodo target
    
    const paramsPosition = this._injector.get(RoutingDataService).getParamsPositions(node.url);
    this._debug(`BreadcrumbsComponent.breadcrumbClick(${node.url})`, 'paramsPosition', paramsPosition)
    const params = this._injector.get(RoutingDataService).extractUrlParamsFromEffectiveUrl(this._injector.get(RoutingDataService).currentUrl, paramsPosition)
    this._debug(`BreadcrumbsComponent.breadcrumbClick(${node.url})`, 'params', params) 
    const targetUrl = this._injector.get(RoutingDataService).setUrlParams(node.url, params)
    this._debug(`BreadcrumbsComponent.breadcrumbClick(${node.url})`, 'targetUrl', targetUrl) 

    this._injector.get(Router).navigate([targetUrl])
  }

  public updateRoutingState() {

    this._setCurrentBreadcrumbs();
    log('RoutingDataService', 'updateRoutingState()', 'currentBreadcrumbs', JSON.stringify(this.currentBreadcrumbs.map(el=>el.id)), 'green')

    this._setSelectedMenuNodeId();
    log('RoutingDataService', 'updateRoutingState()', 'selectedMenuNodeId', this.selectedMenuNodeId, 'green')

    this._setCurrentUrlParams();
    log('RoutingDataService', 'updateRoutingState()', 'currentUrlParams', JSON.stringify(this.currentUrlParams), 'green')
  }

  private _iterateOverTreeAndInitOtherDataStructures(tree: MenuNode[]) {
    let flatNavigationTree: {[id: string]: MenuNode[]} = {}
    let pagesNavigationData: {[id: string]: NavigationData} = {}

    const _flattenNode = (node: MenuNode, path?: MenuNode[]) => {
      path = path || [];
      const chain = JSON.parse(JSON.stringify(path));
      const flatNode = JSON.parse(JSON.stringify(node))
      delete flatNode['children']
      chain.push(flatNode);
      if(node.children && node.children.length > 0) {
        node.children.forEach(el => {
          _flattenNode(el, chain)
        });
      } 
      if(node.navigable) {
        flatNavigationTree[node.id] = chain;
        pagesNavigationData[node.id] = {};
      }
    }

    tree.forEach(el => {
      _flattenNode(el)
    });

    this.flatNavigationTree = flatNavigationTree
    this.pagesNavigationData = pagesNavigationData
  }

  /**
   * Funzione di utility che confronta url effettiva (con url params già applicati) con una url template (con url params non applicati)
   * Calcola la posizione dei parametri analizzando la url template e mette dei placeholder sia alla url template che alla url effettiva (sulla base delle posizioni calcolate)
   * A quel punto effetta un confronto
   */
  public compareUrlTemplateWithEffectiveUrl(urlTemplate, effectiveUrl): boolean{
    debug('RoutingDataService', 'compareUrlTemplateWithEffectiveUrl()', `urlTemplate: ${urlTemplate} - effectiveUrl: ${effectiveUrl}`, '', 'green')
    let placeholdersPositions: number[] = [];

    //Sostituisce tutti gli url params con un placeholder:
    //es.PRIMA currentNodeUrl='private/iam/:id/edit-user-account-group'
    urlTemplate = urlTemplate.split('/').map((el,i)=>{
      if(el.startsWith(":")){
        //dynamic url segment (es. /users/:id -> :id)
        placeholdersPositions.push(i);
        return '[PLACEHOLDER]';
      } else
        return el
      }
    ).join('/')
    //es.DOPO currentNodeUrl='private/iam/[PLACEHOLDER]/edit-user-account-group'

    //Sostituisce tutti gli url params con un placeholder:
    //es.PRIMA urlString='private/iam/10/edit-user-account-group?locale=it-IT&param1=param1'
    effectiveUrl = effectiveUrl.split('?')[0]; //remove query params
    //es.PRIMA urlString='private/iam/10/edit-user-account-group
    effectiveUrl  = effectiveUrl.split('/').map((el,i)=>{
      if(placeholdersPositions.includes(i)){
        return '[PLACEHOLDER]';
      } else
        return el
      }
    ).join('/')
    //es.DOPO urlString='private/iam/[PLACEHOLDER]/edit-user-account-group'

    return urlTemplate == effectiveUrl
  }

  /**
   * Ritorna una array contenente le posizioni dei nodi dell'url template in cui ci sono dei param placeholder.
   * es.
   *    urlTemplate -> '/iam/roles/:id/edit'
   * ritorna
   *    [3]
   */
  public getParamsPositions(urlTemplate): number[]{
    let placeholdersPositions: number[] = [];
    urlTemplate.split('/').forEach((el,i)=>{
      if(el.startsWith(":")){
        placeholdersPositions.push(i)
      }
    })
    return placeholdersPositions;
  }

  /**
   * Ritorna i parametri presenti nell'url, basandosi sull'array contenente le posizioni in cui andarli a prendere.
   * es.
   *    url             -> '/iam/roles/123/edit'
   *    paramsPositions -> [3]
   * ritorna
   *    [123]
   */
  public extractUrlParamsFromEffectiveUrl(url, paramsPositions){
    let params: string[] = [];
    url.split('/').forEach((el,i)=>{
      if(paramsPositions.includes(i)){
        params.push(el)
      }
    })
    return params;
  }

  /**
   * Sostituisce i placeholder dell'url template con i parametri passati in input
   * es.
   *    urlTemplate -> '/iam/roles/:id/edit'
   *    params -> ['123']
   * ritorna
   *    ['/iam/roles/123/edit']
   */
  public setUrlParams(urlTemplate, params: string[]): string {
    let url = urlTemplate.split('/').map(el=>{
      if(el.startsWith(':')){
        const param = params[0];
        params = params.slice(1);
        return param;
      } else
        return el
    }).join('/')
    return url;
  }

  private _setCurrentRoutingNodeId(urlString) {
    // Cerca il nodo navigabile la cui URL coincide con quella corrente
    Object.keys(this.flatNavigationTree).some((key) => {
      const currentNode = this.flatNavigationTree[key]
      let currentNodeUrl = currentNode[currentNode.length-1].url

      if(this.compareUrlTemplateWithEffectiveUrl(currentNodeUrl, urlString)) {
        this.currentRoutingNodeId = key;
        return true;
      }
      return false;
    });
  }

  private _setCurrentBreadcrumbs() {
    this.currentBreadcrumbs = this.flatNavigationTree[this.currentRoutingNodeId]
  }

  private _setSelectedMenuNodeId() {
    const nodes = JSON.parse(JSON.stringify(this.currentBreadcrumbs));
    let found = false;
    nodes.reverse().forEach((el: MenuNode) => {
      if(!found)
        if(el.showInPublicMenu || el.showInPrivateMenu) {
          this.selectedMenuNodeId = el.id;
          found=true;
      }
    });
    if(!found)
      this.selectedMenuNodeId = null;
  }

  private _setCurrentUrlParams() {
   let paramsSegment = this.currentUrl.split('?');
   
   let result = {};

   if(paramsSegment[1]){
     let rawParams = paramsSegment[1].split('&'); 
     
     rawParams.forEach(param => {
       const paramFragments = param.split('=')
       result[paramFragments[0]]=paramFragments[1]
     });
   }

   this.currentUrlParams = result;
  }
  

  
  private _debug = (func, key, value, primary?: boolean) => {
    let color, background;
    if(primary){
      color = 'white';
      background = 'green';
    }else{
      color = 'black';
      background = 'transparent';
    }
    console.log(`%c RoutingDataService.${func} - ${key}: ${value}`, `background: ${background}; color: ${color}; border:2px solid green; padding: 2px;`);
  }
}